'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import RightArrowIcon from '@public/svgs/right_arrow_2.svg';
import { getApiUrl } from '@/utils';
import { Plus } from 'lucide-react';

/**
 * 사업공고 데이터 인터페이스
 * 
 * 사업공고의 상세 정보와 상태를 정의합니다.
 */
interface Announcement {
  id: string;
  title: string;
  description: string;
  createdAt: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  statusText: string;
  statusClass: string;
  formattedCreatedAt: string;
  formattedStartDate: string;
  formattedEndDate: string;
}

/**
 * 사업공고 섹션 컴포넌트
 * 
 * 홈페이지에 최신 사업공고를 표시하는 컴포넌트입니다.
 * 공고가 있는 경우 상세 정보와 함께 표시하고, 없는 경우 안내 메시지를 보여줍니다.
 * 
 * 주요 기능:
 * - 최신 사업공고 표시
 * - 공고 상태 뱃지 (접수중, 접수마감 등)
 * - 공고 기간 표시
 * - 전체 목록 이동 링크
 * - 데이터 없음 상태 처리
 * - 반응형 디자인
 */
export function AnnouncementSection() {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const response = await fetch(getApiUrl('home/announcements'));

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setAnnouncement(result.data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch announcement:', error);
      }
    };

    fetchAnnouncement();
  }, []);

  // 게시글이 없을 때의 처리
  if (!announcement) {
    return (
      <div className='flex flex-col gap-3 sm:gap-4 h-full lg:col-span-2'>
        {/* 섹션 헤더 */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-primary">사업공고</h2>
          <Link
            href="/announcements"
            className="text-muted hover:text-foreground transition-colors duration-100 w-9 h-9 sm:w-11 sm:h-11 -mr-2 flex items-center justify-center"
          >
            <Plus className="size-8 stroke-2" />
          </Link>
        </div>

        {/* 데이터 없음 상태 표시 */}
        <div className="flex flex-col h-[296px] sm:h-[336px] rounded-xl sm:rounded-2xl border-2 border-border overflow-hidden shadow-md sm:shadow-lg">
          <div className='flex flex-col flex-1 p-4 sm:p-7 pb-4 sm:pb-6'>
            <div className="flex items-center justify-center h-full text-foreground">
              <p className="text-center">
                <span className="block text-xl font-bold mb-1">등록된 사업공고가 없습니다.</span>
                <span className="text-sm text-muted">새로운 공고가 등록되면 여기에 표시됩니다.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-3 sm:gap-4 h-full lg:col-span-2'>
      {/* 섹션 헤더 */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-primary">사업공고</h2>
        <Link
          href="/announcements"
          className="text-muted hover:text-foreground transition-colors duration-100 w-9 h-9 sm:w-11 sm:h-11 -mr-2 flex items-center justify-center"
        >
          <Plus className="size-8 stroke-2" />
        </Link>
      </div>

      {/* 공고 콘텐츠 영역 */}
      <div className="flex flex-col flex-1 h-[296px] sm:h-[336px] rounded-xl sm:rounded-2xl border-2 border-border overflow-hidden shadow-md sm:shadow-lg">
        <div className='flex flex-col flex-1 p-4 sm:p-6 pb-4'>

          {/* 공고 제목 */}
          <h3 className="text-xl sm:text-2xl font-semibold mb-1.5 text-foreground line-clamp-1">
            {announcement.title}
          </h3>

          {/* 공고 상태 뱃지 (공고기간이 설정된 경우에만 표시) */}
          {announcement.startDate && announcement.endDate && (
            <div className="mb-2 sm:mb-3 ">
              <span className={`inline-block border rounded-lg px-2 sm:px-2 py-1 text-xs ${announcement.statusClass}`}>
                {announcement.statusText}
              </span>
            </div>
          )}

          {/* 공고 설명 */}
          <p className="text-muted text-base sm:text-lg mb-3 sm:mb-4 line-clamp-2">
            {announcement.description}
          </p>

          {/* 공고 바로가기 링크 */}
          <Link
            href={`/announcements/${announcement.id}`}
            className="self-end mt-auto text-foreground px-4 sm:px-5 py-2 sm:py-2.5 border border-border rounded-full hover:bg-gray-50 text-sm sm:text-base transition-colors duration-100"
          >
            <span className='mr-1.5 sm:mr-2'>공고 바로가기</span>
            <RightArrowIcon className='inline-block text-muted' width={18} height={12} />
          </Link>
        </div>

        {/* 접수기간 섹션 (공고기간이 설정된 경우에만 표시) */}
        {announcement.startDate && announcement.endDate && (
          <div className='bg-primary text-white text-center py-3 sm:py-4'>
            <p className='text-base sm:text-xl'>
              <span className='font-extrabold mr-2 sm:mr-4'>접수기간</span>
              {announcement.formattedStartDate} ~ {announcement.formattedEndDate}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}